import type { UktzedLevel } from "./api.ts";

/**
 * A 10-digit UKTZED code printed as in the customs tariff: heading, HS subheading, CN subheading, national digits.
 */
const GROUPED_CODE = /(?<![\p{L}\d/-])(\d{4})[  .-]?(\d{2})[  .-]?(\d{2})[  .-]?(\d{2})(?![\p{L}\d-])/gu;

/**
 * Formats one UKTZED code the way Law 2697-IX and the customs tariff sites print it.
 * Ten digits become four groups: `8482 50 00 00`. A value that is not ten digits is returned unchanged,
 * so an article number is left as it was written.
 *
 * @param code - Code from the classification, with or without spaces or dots.
 * @returns The code to show.
 */
export function formatCode(code: string | null | undefined): string {
  const digits = String(code ?? "").replace(/\D/g, "");
  if (digits.length !== 10) {
    return String(code ?? "");
  }
  return `${digits.slice(0, 4)} ${digits.slice(4, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
}

/**
 * Formats every 10-digit UKTZED code in a sentence.
 * Digits that are already grouped, or written with dots or hyphens, use the same spacing.
 * A shorter number, such as an article, is left unchanged.
 *
 * @param text - Sentence that may mention a code.
 * @returns The sentence with codes grouped.
 */
export function formatCodesInText(text: string | null | undefined): string {
  return String(text ?? "").replace(GROUPED_CODE, (_match, heading: string, hs: string, cn: string, national: string) => {
    return `${heading} ${hs} ${cn} ${national}`;
  });
}

/**
 * Digits of a code the user typed, or null when it is not 10 digits.
 *
 * @param value - Code with or without spaces, dots, or dashes.
 * @returns Ten digits, or null.
 */
export function normalizeCode(value: string | null | undefined): string | null {
  const digits = String(value ?? "").replace(/\D/g, "");
  return digits.length === 10 ? digits : null;
}

/**
 * Formats the code of any tariff level the way the tariff prints it: `84`, `8482`, `8482 10`, `8482 10 90`, and
 * ten digits through {@link formatCode}. Any other value is returned unchanged.
 *
 * @param code - Chapter or line digits, with or without spaces.
 * @returns The code to show.
 */
export function formatTariffCode(code: string | null | undefined): string {
  const digits = String(code ?? "").replace(/\D/g, "");
  if (digits.length === 10) {
    return formatCode(digits);
  }
  if (digits.length === 2 || digits.length === 4) {
    return digits;
  }
  if (digits.length === 6 || digits.length === 8) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 6)}${digits.length === 8 ? ` ${digits.slice(6, 8)}` : ""}`;
  }
  return String(code ?? "");
}

const NUMERALS: Array<[number, string]> = [[10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"]];

/**
 * The Roman numeral the tariff prints for a section (`16` → `XVI`). The dictionary uses it as the section's id.
 *
 * @param section - Section number, 1 to 39.
 * @returns The numeral, or an empty string outside that range.
 */
export function sectionNumeral(section: number | string): string {
  let value = Number(section);
  if (!Number.isInteger(value) || value < 1 || value > 39) {
    return "";
  }
  let numeral = "";
  for (const [amount, letters] of NUMERALS) {
    while (value >= amount) {
      numeral += letters;
      value -= amount;
    }
  }
  return numeral;
}

/**
 * The section number of a Roman numeral (`XVI` → 16). Only the form {@link sectionNumeral} writes is accepted.
 *
 * @param numeral - Latin letters, any case.
 * @returns The number, or null when the value is not a section numeral.
 */
export function sectionNumber(numeral: string): number | null {
  const upper = numeral.trim().toUpperCase();
  if (!/^[IVX]+$/.test(upper)) {
    return null;
  }
  for (let value = 1; value <= 39; value += 1) {
    if (sectionNumeral(value) === upper) {
      return value;
    }
  }
  return null;
}

/** A part of a note's text that names a tariff node. `id` is the node id, as in `UktzedNode.id`. */
export type NoteReference = { text: string; id: string };

/** A printed code anywhere (`8465 20`, `3926 90 97 20`), or the word that introduces plain numbers. */
const REFERENCE = /(?<!\d)(\d{4}(?: \d{2}){1,3})(?!\d)|(позиці\p{L}*|груп\p{L}*|розділ\p{L}*)/giu;
const LIST_START = /\s+/y;
const LIST_SEPARATOR = /\s*(?:,|[–—-]|\s(?:або|і|й|та|чи|до))\s*/y;
const LIST_ITEM = {
  // Four digits that do not start a printed subheading code; that one is matched on its own.
  heading: /\d{4}(?!\d)(?! \d{2}(?!\d))/y,
  chapter: /\d{1,2}(?!\d)/y,
  section: /[IVXІХ]+(?![\p{L}\d])/uy,
};

/**
 * Finds the nodes a legal note names, so the page can link them: printed codes (`8465 20`) anywhere, four-digit headings after
 * "позиція", chapters after "група", and Roman section numbers after "розділ", with the lists and ranges that follow the word
 * (`8457, 8458 або 8465`, `74-76 або 78-81`).
 *
 * @param text - Note text.
 * @returns The text as plain strings and references, in order. Joined, the pieces give the text back.
 */
export function noteReferences(text: string): Array<string | NoteReference> {
  const pieces: Array<string | NoteReference> = [];
  let last = 0;
  const add = (from: number, to: number, id: string) => {
    if (from > last) {
      pieces.push(text.slice(last, from));
    }
    pieces.push({ text: text.slice(from, to), id });
    last = to;
  };
  for (const match of text.matchAll(REFERENCE)) {
    const at = match.index ?? 0;
    if (at < last) {
      continue;
    }
    if (match[1]) {
      add(at, at + match[1].length, match[1].replace(/ /g, ""));
      continue;
    }
    const word = (match[2] ?? "").toLowerCase();
    const kind = word.startsWith("розділ") ? "section" : word.startsWith("груп") ? "chapter" : "heading";
    const item = LIST_ITEM[kind];
    LIST_START.lastIndex = at + word.length;
    if (!LIST_START.test(text)) {
      continue;
    }
    let cursor = LIST_START.lastIndex;
    for (;;) {
      item.lastIndex = cursor;
      const found = item.exec(text);
      const id = found ? referenceId(kind, found[0]) : null;
      if (!found || !id) {
        break;
      }
      add(cursor, cursor + found[0].length, id);
      cursor += found[0].length;
      LIST_SEPARATOR.lastIndex = cursor;
      if (!LIST_SEPARATOR.test(text)) {
        break;
      }
      const next = LIST_SEPARATOR.lastIndex;
      item.lastIndex = next;
      if (!item.test(text)) {
        break;
      }
      cursor = next;
    }
  }
  if (last < text.length) {
    pieces.push(text.slice(last));
  }
  return pieces;
}

function referenceId(kind: keyof typeof LIST_ITEM, value: string): string | null {
  if (kind === "chapter") {
    return value.padStart(2, "0");
  }
  if (kind === "section") {
    // Notes sometimes print the numeral with Cyrillic І and Х.
    const numeral = value.replace(/І/g, "I").replace(/Х/g, "X");
    return sectionNumber(numeral) === null ? null : numeral;
  }
  return value;
}

const LEVEL_BY_DIGITS: Record<number, UktzedLevel> = { 2: "chapter", 4: "heading", 6: "subheading", 8: "category", 10: "subcategory" };

/**
 * The level of a dictionary node id: a section numeral, two chapter digits, or 4, 6, 8, or 10 line digits.
 *
 * @param id - Node id, as in `UktzedNode.id`.
 * @returns The level, or null when the id has no level.
 */
export function uktzedLevel(id: string): UktzedLevel | null {
  if (sectionNumber(id) !== null) {
    return "section";
  }
  if (!/^\d+$/.test(id)) {
    return null;
  }
  return LEVEL_BY_DIGITS[id.length] ?? null;
}
